import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bookmark, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { useVisitor } from "@/lib/visitor";
import ResultsGrid, { GridSkeleton } from "@/components/lux/ResultsGrid";
import SafetyCallout from "@/components/lux/SafetyCallout";

export default function SavedPage() {
  useSeo({ title: "Saved profiles | LUX", ...PRIVATE_SEO });
  const { saved, removeSaved, clearSaved } = useVisitor();
  const key = saved.join(",");
  const { data, isLoading } = useQuery({
    queryKey: ["saved-cards", key],
    queryFn: async () => (await api.post("/public/cards", { slugs: saved })).data,
    enabled: saved.length > 0,
  });
  const missing = data?.missing || [];
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <div className="pb-6 pt-10 sm:pt-14">
        <nav className="text-xs text-lux-muted" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-lux-silverBright">Home</Link> <span className="mx-1.5">/</span> <span className="text-lux-text">Saved</span>
        </nav>
        <p className="lux-label mt-6">Your shortlist</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-serif text-5xl leading-none sm:text-6xl" data-testid="saved-title">Saved profiles</h1>
          {saved.length > 0 && (
            <button onClick={clearSaved} className="lux-btn lux-btn-ghost lux-btn-sm" data-testid="saved-clear-button"><Trash2 className="h-4 w-4" /> Clear shortlist</button>
          )}
        </div>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-lux-muted" data-testid="saved-privacy-note">
          Saved on this device only. LUX keeps no account, history or record of visitors - clearing your browser data clears this list too.
        </p>
      </div>

      {saved.length === 0 ? (
        <div className="lux-card flex flex-col items-center px-6 py-16 text-center" data-testid="saved-empty">
          <Bookmark className="h-8 w-8 text-lux-silver" aria-hidden="true" />
          <h2 className="mt-4 font-serif text-2xl">Nothing saved yet</h2>
          <p className="mt-2 max-w-sm text-sm text-lux-muted">Tap the bookmark on any profile to keep it here while you browse.</p>
          <Link to="/london" className="lux-btn lux-btn-primary mt-6" data-testid="saved-browse-link">Browse London</Link>
        </div>
      ) : isLoading ? (
        <GridSkeleton n={Math.min(saved.length, 8)} />
      ) : (
        <>
          <ResultsGrid cards={data?.cards} loading={false} showCity emptyTitle="These profiles are no longer listed" emptyText="Saved profiles only appear while their listing is live and verified." />
          {missing.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-[10px] border border-lux-border bg-lux-surface2 p-4 text-sm text-lux-muted" data-testid="saved-missing-note">
              <span>{missing.length} saved profile{missing.length === 1 ? " is" : "s are"} no longer listed (expired, paused or removed).</span>
              <button onClick={() => removeSaved(missing)} className="lux-link ml-auto text-sm" data-testid="saved-remove-missing-button">Remove from shortlist</button>
            </div>
          )}
        </>
      )}
      <div className="mt-20"><SafetyCallout compact /></div>
    </div>
  );
}
